import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from './UserContext';
import { API_URL } from '../constants/api';
import { trackEvent, Events } from '../lib/posthog';

const CreditsContext = createContext(null);

export function CreditsProvider({ children, initData }) {
  const { deviceId, setIsAdmin } = useUser();
  const [credits, setCredits] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const paymentProcessed = useRef(false);

  // Update from initData when it loads
  useEffect(() => {
    if (initData?.loaded) {
      setCredits(initData.credits || 0);
      if (initData.hasSubscription) {
        setSubscription({
          status: initData.subscriptionStatus,
          renewalDate: initData.subscriptionEnd,
        });
        setIsSubscriber(true);
      }
      localStorage.setItem('swingslot_credits', (initData.credits || 0).toString());
    }
  }, [initData]);

  // Deduct credit - uses functional update to avoid stale closure
  const deductCredit = useCallback(async () => {
    if (!deviceId) return false;

    try {
      const response = await fetch(`${API_URL}/api/user/${deviceId}/deduct-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setCredits(data.credits);
        localStorage.setItem('swingslot_credits', data.credits.toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deducting credit:', error);
      // Use functional update to get current credits value (avoids stale closure)
      setCredits(currentCredits => {
        const newCredits = Math.max(0, currentCredits - 1);
        localStorage.setItem('swingslot_credits', newCredits.toString());
        return newCredits;
      });
      return true;
    }
  }, [deviceId]);

  // Redeem SuperAdmin code
  const redeemSuperAdmin = useCallback(async (code) => {
    if (!deviceId) return { success: false, error: 'Device not ready' };

    try {
      const response = await fetch(`${API_URL}/api/redeem-superadmin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, deviceId })
      });

      const data = await response.json();
      if (data.success) {
        setCredits(data.totalCredits);
        localStorage.setItem('swingslot_credits', data.totalCredits.toString());
        if (setIsAdmin) setIsAdmin(true);
        trackEvent('superadmin_activated');
        return { success: true, message: data.message };
      }
      return { success: false, error: data.error || 'Invalid code' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }, [deviceId, setIsAdmin]);

  // Purchase credits
  const purchaseCredits = useCallback(async (packageType) => {
    trackEvent(Events.CHECKOUT_STARTED, { packageType });

    try {
      setShowPricingModal(false);

      if (!deviceId) {
        alert('Session not ready. Please refresh the page.');
        return;
      }

      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packageType, deviceId })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        alert('Payment service unavailable. Please try again.');
        return;
      }

      const data = await response.json();
      if (!response.ok || data.error) {
        alert(data.message || 'Unable to start checkout.');
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Unable to process payment. Please try again.');
    }
  }, [deviceId]);

  // Handle Stripe checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (paymentProcessed.current) return;

    if (success === 'true' && deviceId) {
      paymentProcessed.current = true;
      window.history.replaceState({}, '', window.location.pathname);

      const refreshCredits = async () => {
        // Get initial credits to detect change
        const initialCredits = parseInt(localStorage.getItem('swingslot_credits') || '0', 10);

        // More retries with longer delays for webhook processing
        for (let attempt = 1; attempt <= 5; attempt++) {
          // Delays: 2s, 3s, 4s, 5s, 6s (total 20s of waiting)
          await new Promise(resolve => setTimeout(resolve, (attempt + 1) * 1000));
          try {
            const response = await fetch(`${API_URL}/api/user/${deviceId}/credits`, {
              credentials: 'include'
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.credits !== undefined && data.credits > initialCredits) {
              setCredits(data.credits);
              localStorage.setItem('swingslot_credits', data.credits.toString());
              trackEvent(Events.PURCHASE_COMPLETED, { credits: data.credits });
              return; // Success!
            }
          } catch (error) {
            console.error(`Credit refresh attempt ${attempt} failed:`, error);
          }
        }
        // All retries failed - notify user
        console.error('Credit refresh failed after all attempts');
        alert('Your payment was successful! Credits may take a moment to appear. Please refresh the page if they don\'t show up.');
      };
      refreshCredits();
    } else if (canceled === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [deviceId]);

  const value = {
    credits,
    setCredits,
    remainingAnalyses: credits,
    subscription,
    isSubscriber,
    showPricingModal,
    setShowPricingModal,
    deductCredit,
    purchaseCredits,
    redeemSuperAdmin,
    fetchCredits: initData?.refetch,
    fetchSubscription: initData?.refetch,
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCreditsContext() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error('useCreditsContext must be used within a CreditsProvider');
  }
  return context;
}
